import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL, STORAGE_KEYS, API_ENDPOINTS } from '../config/api';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

interface LoginResponse {
  token: string;
  refreshToken: string;
  userInfo: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
  };
}

class HttpClient {
  private instance: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: unknown) => void;
    reject: (error?: unknown) => void;
  }> = [];

  constructor() {
    this.instance = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor — добавляем токен
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
        if (token && config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: AxiosError) => Promise.reject(error)
    );

    // Response interceptor — обработка 401 + refresh
    this.instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError<ApiResponse<unknown>>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (
          error.response?.status === 401 &&
          originalRequest &&
          !originalRequest._retry &&
          !originalRequest.url?.includes('/auth/refresh-token') &&
          !originalRequest.url?.includes('/auth/login')
        ) {
          const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
          const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);

          if (!token || !refreshToken) {
            return Promise.reject(error);
          }

          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(() => this.instance(originalRequest));
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            const refreshResponse = await axios.post<ApiResponse<LoginResponse>>(
              `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH_TOKEN}`,
              { token, refreshToken },
              { headers: { 'Content-Type': 'application/json' } }
            );

            if (refreshResponse.data.success && refreshResponse.data.data) {
              const newTokens = refreshResponse.data.data;
              localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newTokens.token);
              localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newTokens.refreshToken);
              localStorage.setItem(STORAGE_KEYS.USER_INFO, JSON.stringify(newTokens.userInfo));

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${newTokens.token}`;
              }

              this.processQueue(null);
              return this.instance(originalRequest);
            }

            throw new Error('Ошибка обновления токена');
          } catch (refreshError) {
            this.processQueue(refreshError);
            this.clearAuthData();
            this.notifyAuthStateChanged();
            return Promise.reject(error);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private processQueue(error: unknown): void {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error);
      } else {
        promise.resolve();
      }
    });
    this.failedQueue = [];
  }

  private clearAuthData(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER_INFO);
  }

  private notifyAuthStateChanged(): void {
    import('../store/auth.store').then(({ useAuthStore }) => {
      useAuthStore.getState().setUser(null);
    });
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.get<ApiResponse<T>>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.post<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.put<ApiResponse<T>>(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.instance.delete<ApiResponse<T>>(url, config);
    return response.data;
  }
}

export const httpClient = new HttpClient();
